import React from 'react';
import { motion } from 'framer-motion';
import { getCheckoutUrl } from '../utils/utm';

export const MechanismSection = () => {
  const checkoutUrl = getCheckoutUrl();

  return (
    <section 
      data-testid="mechanism-section"
      id="mechanism-section"
      className="section-padding px-8 md:px-12 bg-white"
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.7 }}
          className="max-w-3xl"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-[2px] bg-amber-600" />
            <p className="font-sans text-xs tracking-widest uppercase text-gray-400">
              How the System Works
            </p>
          </div>
          
          <h2 className="font-editorial text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight mb-6">
            Most Investors Pick Stocks.{' '}
            <span className="text-amber-600">Charles Teaches You to Pick the Right Market First.</span>
          </h2>
        </motion.div>

        {/* Body Copy */}
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.2 }}
          className="max-w-3xl font-sans text-lg text-gray-600 leading-relaxed space-y-6 mt-10 mb-16"
        >
          <p>
            Here is what most investors do not know: the top-performing sectors shift dramatically from year to year. One year it is utilities leading the market. The next year, financials. The year after that, consumer discretionary. These rotations are not random. They are driven by economic cycles, policy changes, and macroeconomic conditions that experienced investors learn to read and position around.
          </p>
          <p>
            Most retail investors choose stocks without ever asking which sector is currently leading. That is like knowing which runner to bet on while ignoring which direction the wind is blowing.
          </p>
        </motion.div>

        {/* Framework Grid */}
        <div className="grid md:grid-cols-2 gap-12 md:gap-16">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
            data-testid="framework-layer-1"
            className="relative pl-8 border-l-2 border-gray-100 hover:border-amber-600 transition-colors duration-300"
          >
            <div className="absolute -left-[9px] top-0 w-4 h-4 bg-amber-600 rounded-full" />
            <span className="font-editorial text-6xl font-bold text-gray-100">01</span>
            <h3 className="font-editorial text-2xl font-bold text-gray-900 mt-2 mb-4">Layer One: Sector Awareness</h3>
            <p className="font-sans text-gray-600 leading-relaxed">
              Payne's Education starts here. Charles teaches you how to read sector momentum and identify where the strongest opportunities are concentrated before you ever evaluate a single company.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
            data-testid="framework-layer-2"
            className="relative pl-8 border-l-2 border-gray-100 hover:border-amber-600 transition-colors duration-300"
          >
            <div className="absolute -left-[9px] top-0 w-4 h-4 bg-amber-600 rounded-full" />
            <span className="font-editorial text-6xl font-bold text-gray-100">02</span>
            <h3 className="font-editorial text-2xl font-bold text-gray-900 mt-2 mb-4">Layer Two: Three-Pillar Selection</h3>
            <p className="font-sans text-gray-600 leading-relaxed">
              Charles's three-pillar approach to stock selection within those leading sectors combines fundamental company analysis, macroeconomic context, and disciplined portfolio construction criteria to identify the strongest positions in the parts of the market that are already showing leadership.
            </p>
          </motion.div>
        </div>

        {/* Visual Callout Box */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.3 }}
          className="mt-12 bg-gray-50 border-l-4 border-amber-600 p-8"
        >
          <p className="font-sans text-gray-600 leading-relaxed">
            The result is a double advantage. You enter the right part of the market at the right time. Then you select the right companies within it. That combination is how experienced investors build positions that perform over months and years rather than chasing trades that reverse in days.
          </p>
          <p className="font-sans text-sm font-semibold text-gray-900 mt-4">
            The outcome: A structured process for knowing where to look, what to look for, and when the thesis changes.
          </p>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mt-16 pt-12 border-t border-gray-100 flex flex-col sm:flex-row items-start sm:items-center gap-6"
        >
          <a
            href={checkoutUrl}
            data-testid="mechanism-cta-button"
            className="btn-primary"
          >
            Get the Framework
          </a>
          <button
            onClick={() => { const el = document.getElementById('offer-details'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            data-testid="mechanism-whats-included-btn"
            className="group flex items-center gap-2 font-sans text-sm text-amber-600 hover:text-amber-700 font-medium transition-colors"
          >
            <span>See Everything Included Below</span>
            <svg className="w-4 h-4 group-hover:translate-y-1 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" /></svg>
          </button>
        </motion.div>
      </div>
    </section>
  );
};
